import React, { useEffect, useMemo, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { adminAPI } from '../../services/authService';
import { notifyError } from '../../utils/notify';

const EMPTY_AUDIT_FILTERS = {
  entityType: '',
  action: '',
  actorEmail: '',
  projectId: '',
  dateFrom: '',
  dateTo: '',
};

const formatDateTimeIN = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN');
};

const prettyJson = (value) => {
  if (value == null) return '-';
  try {
    return JSON.stringify(value, null, 2);
  } catch (_e) {
    return String(value);
  }
};

const truncate = (text, max = 120) => {
  const s = String(text || '').trim();
  if (!s) return '—';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const AuditLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ ...EMPTY_AUDIT_FILTERS });

  const load = async (activeFilters = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.entityType) params.entityType = activeFilters.entityType;
      if (activeFilters.action) params.action = activeFilters.action;
      if (activeFilters.actorEmail) params.actorEmail = activeFilters.actorEmail;
      if (activeFilters.projectId) params.projectId = Number(activeFilters.projectId);
      if (activeFilters.dateFrom) params.dateFrom = activeFilters.dateFrom;
      if (activeFilters.dateTo) params.dateTo = activeFilters.dateTo;
      const response = await adminAPI.getAuditLogs(params);
      const { rows: items } = unwrapListResponse(response);
      setRows(safeArray(items));
      setSelected(null);
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Unable to load audit logs.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityOptions = useMemo(() => {
    const s = new Set(safeArray(rows).map((x) => x.entityType).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const letterSubjectCell = (item) => {
    const letterRef = item.letterRef || item.correspondence?.letterRef;
    const subject = item.subject || item.correspondence?.subject;
    if (letterRef && subject) {
      return (
        <div>
          <div style={{ fontWeight: 600 }}>Ref {letterRef}</div>
          <div style={{ fontSize: 12, color: '#4b5563' }} title={subject}>{truncate(subject, 90)}</div>
        </div>
      );
    }
    if (letterRef) return <span style={{ fontWeight: 600 }}>Ref {letterRef}</span>;
    if (subject) return <span title={subject}>{truncate(subject, 100)}</span>;
    if (item.correspondence?.chainId) return <span style={{ color: '#6b7280' }}>Subject #{item.correspondence.chainId}</span>;
    return '—';
  };

  return (
    <div>
      <div className="section-header">
        <h2>Audit Log</h2>
        <button className="btn-secondary" type="button" onClick={load} disabled={loading}>Refresh</button>
      </div>

      <div className="panel audit-filter-panel" style={{ marginBottom: 12 }}>
        <div className="form-row">
          <div className="form-group">
            <label>Entity</label>
            <select value={filters.entityType} onChange={(e) => setFilters((p) => ({ ...p, entityType: e.target.value }))}>
              <option value="">All</option>
              {entityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Action</label>
            <select value={filters.action} onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}>
              <option value="">All</option>
              <option value="CREATE">CREATE</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div className="form-group">
            <label>Actor Email</label>
            <input value={filters.actorEmail} onChange={(e) => setFilters((p) => ({ ...p, actorEmail: e.target.value }))} placeholder="admin@company.com" />
          </div>
          <div className="form-group">
            <label>Project ID</label>
            <input type="number" min="1" value={filters.projectId} onChange={(e) => setFilters((p) => ({ ...p, projectId: e.target.value }))} placeholder="101" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Date From</label>
            <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Date To</label>
            <input type="date" value={filters.dateTo} onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} />
          </div>
          <div className="form-group filter-panel-actions">
            <button className="btn-primary" type="button" onClick={() => load()} disabled={loading}>Apply Filters</button>
            <ResetFiltersButton
              onClick={() => {
                setFilters({ ...EMPTY_AUDIT_FILTERS });
                load({ ...EMPTY_AUDIT_FILTERS });
              }}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Record</th>
              <th>Letter / Subject</th>
              <th>What changed</th>
              <th>Project</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 18, color: '#6b7280' }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 18, color: '#6b7280' }}>No audit records found.</td></tr>
            ) : safeArray(rows).map((item) => (
              <tr key={item.id} className="clickable" onClick={() => setSelected(item)}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTimeIN(item.createdAt)}</td>
                <td>{item.action}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>{item.entityLabel || item.entityType}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {item.recordLabel ? item.recordLabel : (item.entityId != null ? `#${item.entityId}` : '—')}
                  </div>
                </td>
                <td>{letterSubjectCell(item)}</td>
                <td title={item.changeSummary || ''} style={{ maxWidth: 280 }}>{truncate(item.changeSummary, 140)}</td>
                <td>{item.projectId ?? '—'}</td>
                <td>{item.actorEmail || item.actorName || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="section-header">
            <h3 style={{ margin: 0 }}>Audit Details #{selected.id}</h3>
            <button className="btn-secondary" type="button" onClick={() => setSelected(null)}>Close</button>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Action</label><input value={selected.action || ''} readOnly /></div>
            <div className="form-group"><label>Record type</label><input value={selected.entityLabel || selected.entityType || ''} readOnly /></div>
            <div className="form-group"><label>Record</label><input value={selected.recordLabel || selected.entityId || ''} readOnly /></div>
            <div className="form-group"><label>Actor</label><input value={selected.actorEmail || selected.actorName || '—'} readOnly /></div>
          </div>
          {(selected.letterRef || selected.subject || selected.correspondence) && (
            <div className="form-row">
              <div className="form-group">
                <label>Letter ref</label>
                <input value={selected.letterRef || selected.correspondence?.letterRef || '—'} readOnly />
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label>Correspondence subject</label>
                <input value={selected.subject || selected.correspondence?.subject || '—'} readOnly />
              </div>
            </div>
          )}
          {safeArray(selected.changes).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px' }}>Field changes</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.changes.map((c) => (
                    <tr key={`${c.field}-${c.label}`}>
                      <td>{c.label}</td>
                      <td style={{ fontSize: 13 }}>{c.from}</td>
                      <td style={{ fontSize: 13 }}>{c.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Before (raw)</label>
              <textarea rows={8} value={prettyJson(selected.beforeData)} readOnly />
            </div>
            <div className="form-group">
              <label>After (raw)</label>
              <textarea rows={8} value={prettyJson(selected.afterData)} readOnly />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
